/**
 * What the applicant screens ask for, and what they claim, in one place.
 *
 * Two rules live side by side here and must stay apart:
 *
 * - **The account tier** (every session the server does not mark otherwise):
 *   at least 4 pieces of evidence, at least 1 of them a vouch invite. The
 *   server publishes its numbers for this rule too, and the screen shows them
 *   (`serverSubmissionRule`); the arithmetic below (0-1 accounts -> 3, 2 -> 2,
 *   3+ -> 1, Submit at "4 of 4 tracks") is only the fallback for a backend that
 *   does not publish them yet.
 *
 * - **The Flow rule**: the session's workflow states its own vouch number. The
 *   screen does not decide this. The server says so on the status payload
 *   (`vouch_summary.required_by === "flow"`) and publishes both the number and
 *   whether its own submit gate would pass (`can_submit`). The screen reads
 *   those instead of re-deriving them, so what the applicant is told and what
 *   the server accepts cannot disagree.
 */

import type { SessionStatus } from "@/lib/api";
import { getRequiredVouches, serverVouchNumbers } from "@/lib/submissionEligibility";

export interface FlowSubmissionRule {
  /** How many vouch invites the Flow asks for. */
  requiredVouches: number;
  /** The server's submit gate: required steps satisfied AND enough invites sent. */
  canSubmit: boolean;
}

/**
 * The Flow rule for this session, or `null` for the account tier.
 *
 * Null whenever the server did not name the Flow as the source of the number,
 * including every response from a backend older than the field. Falling back is
 * the safe direction: it is today's behaviour.
 */
export function flowSubmissionRule(
  session: Pick<SessionStatus, "vouch_summary" | "can_submit"> | null | undefined,
): FlowSubmissionRule | null {
  const summary = session?.vouch_summary;
  if (!summary || summary.required_by !== "flow") return null;
  if (typeof summary.required !== "number") return null;
  return { requiredVouches: summary.required, canSubmit: session?.can_submit === true };
}

/** The server's numbers for ANY session, plus the piece count behind them. */
export interface ServerSubmissionRule extends FlowSubmissionRule {
  /** Pieces of evidence the gate counts: accounts plus qualifying email addresses. */
  evidencePieces: number;
}

/**
 * The server's vouch numbers on every session, account tier included: how
 * many invites to ask for (`owed`) and whether Submit opens
 * (`can_submit`). Null on an older backend, which keeps the tier arithmetic.
 *
 * Deliberately separate from `flowSubmissionRule`: that one also decides which
 * LAYOUT a session gets (the track tracker, the "add an account for fewer
 * vouches" nudge, the stepper pips), and a leasing session must keep the
 * leasing layout. This one only supplies the numbers.
 */
export function serverSubmissionRule(
  session: Pick<SessionStatus, "vouch_summary" | "can_submit"> | null | undefined,
): ServerSubmissionRule | null {
  const n = serverVouchNumbers(session);
  if (!n) return null;
  return { requiredVouches: n.owed, canSubmit: n.canSubmit, evidencePieces: n.evidencePieces };
}

/** The number of vouch invites the vouch screen asks for. */
export function requiredVouchInvites(rule: FlowSubmissionRule | null, accountChecks: number): number {
  return rule ? rule.requiredVouches : getRequiredVouches(accountChecks);
}

/** Whether Submit is offered. On the tier, the "4 of 4 tracks" test, unchanged. */
export function isSubmitReady(rule: FlowSubmissionRule | null, tracksFilled: number): boolean {
  return rule ? rule.canSubmit : tracksFilled >= 4;
}

/**
 * The "N of 4 tracks verified" strip is tier arithmetic (accounts + invites,
 * capped at 4). On a Flow it counts things the Flow never asked for and ignores
 * the one it did (the email), so it is not shown there.
 */
export function showsTrackTracker(rule: FlowSubmissionRule | null): boolean {
  return rule === null;
}

/**
 * "Add an account to need fewer vouches" is only true on the tier. On a Flow,
 * accounts do not change the number.
 */
export function offersAccountsForFewerVouches(rule: FlowSubmissionRule | null, requiredVouches: number): boolean {
  return rule === null && requiredVouches > 1;
}

/**
 * Whether a full tracker paints skipped steps green in the stepper. On a Flow
 * the stepper reads the server's own step evidence, and an amber pip over a
 * skipped optional step is the true state, so nothing overrides it.
 */
export function promotesSkippedSteps(rule: FlowSubmissionRule | null, trackerReady: boolean): boolean {
  return rule === null && trackerReady;
}

/**
 * Why Continue on the vouch screen is disabled, in words. "more" only once
 * someone has already been invited.
 */
export function continueBlockedMessage(needed: number, sent: number): string {
  const more = sent > 0 ? "more " : "";
  const who = needed === 1 ? "person who knows you" : "people who know you";
  return `Invite ${needed} ${more}${who} to continue.`;
}

/** What is left before a Flow session can submit, as one plain sentence. */
export function outstandingBeforeSubmit(left: {
  emailOutstanding: boolean;
  accountOutstanding: boolean;
  idOutstanding: boolean;
  vouchesNeeded: number;
  vouchesSent: number;
}): string {
  const items: string[] = [];
  if (left.emailOutstanding) items.push("verify your email");
  if (left.accountOutstanding) items.push("connect a digital account");
  if (left.idOutstanding) items.push("finish the ID check");
  if (left.vouchesNeeded > 0) {
    const more = left.vouchesSent > 0 ? "more " : "";
    const who = left.vouchesNeeded === 1 ? "person who knows you" : "people who know you";
    items.push(`invite ${left.vouchesNeeded} ${more}${who}`);
  }
  if (items.length === 0) return "Finish the steps above before submitting.";
  const list =
    items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
  return `Before you submit, ${list}.`;
}

/**
 * #604 — the application email card on the email step.
 *
 * The address comes from the application form. Opening the link records
 * nothing, so nothing on the server says this address was verified, and the
 * card must not say so either. It states the address and how to confirm it;
 * once the email step is satisfied it says the STEP is complete. It still
 * does not say this address is verified, because the step can be satisfied
 * by signing in with an account under a different address.
 *
 * ⚠️ **Applies to every applicant session, not only a Flow.** Decided
 * 2026-09-24 (👤 Juan, platform#604): "for an applicant, clicking a link we
 * emailed is not proof of the email address... Nothing else counts." That
 * rule names "an applicant," with no Flow/tier carve-out — the question it
 * answers ("does a link click count as evidence for THIS PERSON") is the
 * same one on both paths.
 *
 * Until this fix, the tier (leasing) path returned `claimsVerified: true`
 * unconditionally whenever `!definitionDriven`, showing a green "✓ Verified"
 * badge purely because `applicantEmail` was present — with the caller
 * (`ApplicantFlow.tsx`) already computing the real
 * `stepHasEvidence[APPLICANT_STEP.EMAIL]` evidence for both branches and the
 * tier branch simply not consulting it. That was a deliberate, temporary
 * carve-out at the time (PR #209, 2026-09-21, "a separate decision, not
 * taken here") for a policy question that was still open. It has been
 * answered, so both paths now share the same claim logic. `optionsLabel`
 * keeps its existing wording difference — that was always cosmetic copy
 * ("Ways to verify" vs "Other ways to verify"), never part of the claim.
 */
export function applicationEmailCard(state: {
  definitionDriven: boolean;
  emailStepSatisfied: boolean;
}): { claimsVerified: boolean; note: string | null; optionsLabel: string } {
  return {
    claimsVerified: false,
    note: state.emailStepSatisfied
      ? "Your email step is complete."
      : "Confirm this address with a one-time code, or sign in with one of the accounts below.",
    optionsLabel: state.definitionDriven ? "Ways to verify" : "Other ways to verify",
  };
}

/**
 * The `emailStepSatisfied` every application-email card reads (#604). One
 * value, so the original card and the Article card cannot disagree.
 *
 * - Flow (definition-driven) session: the server's own step evidence
 *   (`flowEmailEvidence`, i.e. `stepHasEvidence[EMAIL]`, which a Flow
 *   overrides with `definitionEvidenceByIndex`).
 * - Tier (leasing) session: NOT `stepHasEvidence[EMAIL]`, whose leasing-era
 *   heuristic counts `applicant_email` itself, the link-click-as-proof claim
 *   #604 forbids. Real evidence instead: a one-time code, or a sign-in that
 *   proved the address (platform#712).
 * - Demo: the demo code panel's verified address stands in.
 */
export function applicationEmailStepSatisfied(state: {
  isLive: boolean;
  definitionDriven: boolean;
  flowEmailEvidence: boolean;
  otpVerifiedCount: number;
  anySignInProvedEmail: boolean;
}): boolean {
  if (!state.isLive) return state.otpVerifiedCount > 0;
  if (state.definitionDriven) return state.flowEmailEvidence;
  return state.otpVerifiedCount > 0 || state.anySignInProvedEmail;
}
